import type { UseQueryResult } from '@tanstack/react-query';
import { useApi, type ApiError } from './api';

/**
 * The public facts a page needs and a browser cannot work out for itself.
 *
 * The client half of `GET /v1/site` — see `apps/api/src/routes/site.ts` for why
 * these come from a server at all rather than from six `VITE_` variables baked
 * into the bundle. Two pages read it, `/contact` and `/download`, which is one
 * more than would justify a hook if the shape were not a contract with an
 * endpoint.
 */

export interface LauncherBuild {
  label: string;
  note: string;
  /** Null until an installer for this platform has been published. */
  url: string | null;
}

export interface SiteConfig {
  /**
   * Who the buyer is actually contracting with, as a finished sentence.
   *
   * Composed on the server from the deployment's payment provider, because it
   * is a consumer-law statement rather than copy: a page claiming GOODBUILD
   * sells to you while somebody else bills you is wrong in a way a legal team
   * notices and a designer does not.
   */
  traderLine: string;
  /** The community invite, or null when none is configured. */
  discordInvite: string | null;
  launcher: {
    version: string | null;
    builds: LauncherBuild[];
  };
}

export interface LegalDocument {
  title: string;
  version: string;
  status: 'draft' | 'published';
  /** Markdown, rendered as the text it is. See `LegalRoute`. */
  body: string;
}

export function useSiteConfig(): UseQueryResult<SiteConfig, ApiError> {
  return useApi<SiteConfig>(['site'], '/site');
}

export function useLegalDocument(
  kind: string,
): UseQueryResult<{ kind: string; document: LegalDocument | null }, ApiError> {
  return useApi<{ kind: string; document: LegalDocument | null }>(
    ['site', 'legal', kind],
    `/site/legal/${encodeURIComponent(kind)}`,
  );
}
